import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useLogin } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import BrandLogo from '../../components/ui/BrandLogo/BrandLogo';
import { IconArrowLeft } from '@tabler/icons-react';
import styles from './AuthPage.module.scss';

const LoginPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{username?: string; password?: string}>({});

  useEffect(() => {
    // Check if user was redirected from registration
    const isRegistered = sessionStorage.getItem('registered');
    if (isRegistered === 'true') {
      showSuccess(t('login.registrationSuccess') || 'Registration successful! Please login.');
      sessionStorage.removeItem('registered');
    }
  }, [t, showSuccess]);

  const loginMutation = useLogin();

  const validateForm = (): boolean => {
    const newErrors: {username?: string; password?: string} = {};

    // Username validation
    if (!username.trim()) {
      newErrors.username = t('login.usernameRequired') || 'Username is required';
    } else if (username.length < 3) {
      newErrors.username = t('login.usernameMinLength') || 'Username must be at least 3 characters';
    } else if (username.length > 30) {
      newErrors.username = t('login.usernameMaxLength') || 'Username must not exceed 30 characters';
    }

    // Password validation
    if (!password) {
      newErrors.password = t('login.passwordRequired') || 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = t('login.passwordMinLength') || 'Password must be at least 8 characters';
    } else if (password.length > 128) {
      newErrors.password = t('login.passwordMaxLength') || 'Password must not exceed 128 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    if (!validateForm()) {
      return;
    }

    loginMutation.mutate(
      { username: username.trim(), password },
      {
        onSuccess: () => {
          showSuccess(
            t('login.success') || 'Login successful!',
            t('login.redirecting') || 'Redirecting to dashboard...'
          );
          setTimeout(() => {
            navigate({ to: '/dashboard' });
          }, 1000);
        },
        onError: (error: any) => {
          console.error('Login error:', error);
          // Generic error message
          showError(
            t('login.failed') || 'Login Failed',
            t('login.invalidCredentials') || 'Invalid username or password. Please try again.'
          );
        }
      }
    );
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.authCard}>
        <div className={styles.logoContainer}>
          <div className={styles.brandLogo}>
              <BrandLogo className={styles.brandLogoImage}/>
          </div>
        </div>
        
        <h2 className={styles.authTitle}>{t('login.title')}</h2>
        
        <form className={styles.authForm} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="username">{t('login.username')}</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (errors.username) setErrors(prev => ({...prev, username: undefined}));
              }}
              placeholder={t('login.usernamePlaceholder')}
              className={errors.username ? styles.inputError : ''}
            />
            {errors.username && (
              <span className={styles.fieldError}>{errors.username}</span>
            )}
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="password">{t('login.password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) setErrors(prev => ({...prev, password: undefined}));
              }}
              placeholder={t('login.passwordPlaceholder')}
              className={errors.password ? styles.inputError : ''}
            />
            {errors.password && (
              <span className={styles.fieldError}>{errors.password}</span>
            )}
          </div>
          
          <button 
            type="submit" 
            className={styles.authButton}
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? t('login.loggingIn') : t('login.submit')}
          </button>
        </form>
        
        <div className={styles.authLinks}>
          <p className={styles.backToHomeLink}>
            <IconArrowLeft size={18} />
            <span className={styles.authLink} onClick={() => navigate({ to: '/' })}>{t('login.backToHome')}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
